import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import type { FormInstance } from 'antd';
import { FieldDefaultsProvider, GradeField, MoneyField, TextField, type FieldDefaults } from '../../../ui/fields';
import { Field, FormModal, FormSection, V } from '../../../ui/forms';
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Popconfirm,
  Select,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { FeeAppliesTo } from '@velora/contracts';
import { StepFooter } from '../StepFooter';
import { productApi } from '../../../api';

/**
 * Setup step 4 — Fee heads. Real add-a-head form + list with running total.
 * The richer grade-band matrix + instalment schedules land with the Fees module
 * (Admin #26). M-01: amounts stored as integer paise, displayed as ₹.
 */
export function FeesSetupPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);

  const list = useQuery({
    queryKey: ['fee-heads', institution],
    queryFn: () => productApi.listFeeHeads(institution),
    enabled: !!institution,
  });
  const del = useMutation({
    mutationFn: (id: string) => productApi.deleteFeeHead(institution, id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['fee-heads', institution] }); void message.success('Fee head removed'); },
  });

  const total = list.data?.total ?? 0;
  const canMarkDone = total > 0;

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ marginBottom: 14 }}>
        <Typography.Title level={2} style={{ margin: 0 }}>
          Fee structure
        </Typography.Title>
        <Typography.Text type="secondary">
          Nothing is billed until you say so. Add the heads that appear on your fee circular.
          Grade bands, instalments, and concessions ship with the full Fees module.
        </Typography.Text>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, marginBottom: 14 }}>
        <Card size="small">
          <Statistic title="Heads defined" value={total} />
        </Card>
        <Card size="small">
          <Statistic
            title="Total (all-grades sum)"
            value={rupees(list.data?.total_amount_paise ?? 0)}
            prefix="₹"
          />
        </Card>
      </div>

      <Card
        size="small"
        title="Fee heads"
        extra={
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
            Add head
          </Button>
        }
      >
        {total === 0 ? (
          <Empty
            description={
              <span>
                No heads yet. <a onClick={() => setAddOpen(true)}>Add the first one</a>.
              </span>
            }
            style={{ padding: 16 }}
          />
        ) : (
          <Table
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={list.data?.rows ?? []}
            columns={[
              { title: 'Name', dataIndex: 'name', key: 'name' },
              {
                title: 'Amount',
                dataIndex: 'amount_paise',
                key: 'amount_paise',
                align: 'right',
                render: (v: number) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>₹{rupees(v)}</span>,
              },
              {
                title: 'Applies to',
                key: 'applies_to',
                render: (_, r) =>
                  r.applies_to === 'all' ? (
                    <Tag>all grades</Tag>
                  ) : (
                    <Tag color="processing">grade {r.grade}</Tag>
                  ),
              },
              {
                key: 'action', width: 40, align: 'right',
                render: (_, r) => (
                  <Popconfirm title="Delete this fee head?" onConfirm={() => del.mutate(r.id)} okText="Delete" okButtonProps={{ danger: true }}>
                    <Button size="small" type="text" icon={<DeleteOutlined />} danger />
                  </Popconfirm>
                ),
              },
            ]}
          />
        )}
      </Card>

      {!canMarkDone && (
        <Alert type="warning" showIcon style={{ marginTop: 12 }} message="Add at least one fee head before marking done." />
      )}

      <StepFooter
        institution={institution}
        step="fees"
        summary={`${total} fee head${total === 1 ? '' : 's'}`}
        disabled={!canMarkDone}
        disabledReason="Add at least one fee head first"
        nextHref={`/i/${institution}/setup/attendance-mode`}
        nextLabel="Next: attendance mode →"
      />

      <AddHeadModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        institution={institution}
        onCreated={() => {
          void qc.invalidateQueries({ queryKey: ['fee-heads', institution] });
          setAddOpen(false);
          void message.success('Fee head added');
        }}
      />
    </div>
  );
}

type FeeHeadFormValues = { name: string; amount_rupees: number; applies_to: FeeAppliesTo; grade?: string };

function AddHeadModal({
  open, onClose, institution, onCreated,
}: { open: boolean; onClose: () => void; institution: string; onCreated: () => void }) {
  const structure = useQuery({
    queryKey: ['structure', institution],
    queryFn: () => productApi.getStructure(institution),
    enabled: !!institution && open,
  });
  const grades = structure.data?.grades ?? [];
  const fieldDefaults = useMemo<FieldDefaults>(
    () => ({ grade: { options: grades.map((g) => ({ value: g.name, label: g.name })) } }),
    [grades],
  );

  return (
    <FormModal<FeeHeadFormValues>
      open={open} onClose={onClose}
      title="Add fee head" submitText="Add"
      initial={{ applies_to: 'all' }}
      mutation={(v) => productApi.createFeeHead(institution, {
        name: v.name,
        amount_paise: Math.round(v.amount_rupees * 100),
        applies_to: v.applies_to,
        grade: v.applies_to === 'grade' ? v.grade : undefined,
      })}
      onSuccess={onCreated}
    >
      {(vform) => (
        <FieldDefaultsProvider value={fieldDefaults}>
          <FeeHeadBody form={vform.form} />
        </FieldDefaultsProvider>
      )}
    </FormModal>
  );
}

function FeeHeadBody({ form }: { form: FormInstance<FeeHeadFormValues> }) {
  const appliesTo = Form.useWatch('applies_to', form);
  return (
    <>
      <Field name="name" label="Head name" required>
        <TextField placeholder="Tuition" autoFocus />
      </Field>
      <FormSection cols={2}>
        <Field name="amount_rupees" label="Amount" required rules={[V.money()]}>
          <MoneyField placeholder="18000" />
        </Field>
        <Field name="applies_to" label="Applies to" required>
          <Select options={[
            { value: 'all', label: 'All grades' },
            { value: 'grade', label: 'One grade only' },
          ]} />
        </Field>
      </FormSection>
      {appliesTo === 'grade' && (
        <Field name="grade" label="Grade" required>
          <GradeField />
        </Field>
      )}
    </>
  );
}

function rupees(paise: number): string {
  return (paise / 100).toLocaleString('en-IN');
}
